import React, { useState, useEffect } from 'react';
import { verificationApi, verificationRunsApi } from '../../utils/api';
import AgentVerdictBadge from '../AgentVerdictBadge';
import { fmtStamp } from '../proposalGrid/helpers';

// PRD-022 WU-D — verification + buyer accept/refute action row, per Outcome,
// on the unified Agreement grid. Matches the action rows in
// agreement-unified-grid-v3.html.
//
// Model (RJ 2026-06-07): verification is performed by the NEUTRAL Token Broker
// agent — not the seller. The seller's job is to PROVIDE EVIDENCE ("Submit
// evidence" → the agent runs run-verification against spec v1); a small
// de-emphasized "Manual override" link is the escape hatch (POST .../verify,
// note ≥ 10). The buyer reviews the neutral verdict and Accepts / Refutes
// (refute note ≥ 10). Settlement waits on acceptance. Visibility matrix (§4):
// the seller never sees Accept/Refute; the buyer never sees Submit/verify.

let _k = 0;
const idemKey = () => `ui-${Date.now()}-${++_k}`;

// Module-scope so they keep a stable component identity across re-renders.
// Defining these INSIDE the component remounts the <textarea> on every
// keystroke (autoFocus then resets the caret to index 0 → text is entered
// reversed). Keep them out here. (Bug fix 2026-06-18.)
function Note({ children }) {
  return <span className="text-[12.5px] text-gray-600">{children}</span>;
}

function NoteBox({ placeholder, onSubmit, submitLabel, tone, note, setNote, busy, onCancel }) {
  return (
    <div className="mt-1.5 w-full max-w-md space-y-1.5">
      <textarea
        className="w-full text-xs bg-white text-gray-900 placeholder-gray-500 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-300"
        rows={2} placeholder={placeholder} value={note} onChange={(e) => setNote(e.target.value)} autoFocus
      />
      <div className="flex items-center gap-2">
        <button disabled={busy} onClick={onSubmit}
          className={`text-xs font-semibold px-2.5 py-1 rounded text-white ${tone === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'}`}>
          {busy ? '…' : submitLabel}
        </button>
        <button disabled={busy} onClick={onCancel} className="text-xs text-gray-600">Cancel</button>
      </div>
    </div>
  );
}

// PRD-031 F2.1 — bounded, never-silent waiting. Thresholds match the
// server-side stuck-run alert (runHealth.js): a run queued past its start
// time + 5 minutes with no daily-cap reason, or a non-terminal run with no
// progress event for 10 minutes, degrades to "taking longer than expected"
// with a retry — "Checking now" never masks a dead pipeline.
const NON_TERMINAL = ['queued', 'planning', 'collecting', 'judging'];
const QUEUED_STUCK_MS = 5 * 60 * 1000;
const MID_RUN_STALL_MS = 10 * 60 * 1000;

// The live-state banner for the Outcome's freshest non-terminal run.
function RunStateLine({ run, isSeller, agreementId, onRunsChanged }) {
  const [nowTick, setNowTick] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [actErr, setActErr] = useState('');
  const countdownActive = run && run.status === 'queued' && run.not_before
    && new Date(run.not_before).getTime() > nowTick;
  useEffect(() => {
    // tick each second while a countdown shows; each 30 s otherwise (the
    // stuck thresholds only need coarse time).
    const t = setInterval(() => setNowTick(Date.now()), countdownActive ? 1000 : 30000);
    return () => clearInterval(t);
  }, [countdownActive]);
  if (!run) return null;
  if (run.status !== 'failed' && run.status !== 'budget_exhausted' && !NON_TERMINAL.includes(run.status)) return null;

  const act = async (fn) => {
    setBusy(true); setActErr('');
    try { await fn(); await onRunsChanged(); } catch (e) {
      setActErr(e.response?.data?.message || e.response?.data?.error || 'That didn’t go through — try again.');
    }
    setBusy(false);
  };
  const startNow = () => act(() => verificationRunsApi.startNow(agreementId, run.id));
  const retry = () => act(() => verificationRunsApi.trigger(agreementId, { deliverable_id: run.deliverable_id || undefined }));
  const line = (children) => (
    <div className="flex items-center gap-2.5 flex-wrap rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[12.5px] text-gray-700">
      {children}
      {actErr && <span className="text-coral-700">{actErr}</span>}
    </div>
  );

  // Budget exhaustion is a terminal STATUS, not an error code — the run
  // stopped on purpose, having spent this Agreement's checking budget. It
  // rendered nothing at all, so the check simply went quiet (QA finding B1).
  if (run.status === 'budget_exhausted') {
    // The run did not stop: it finished, and the criteria it reached carry
    // real verdicts. What ran out is the money to judge the rest, so those
    // fell to a person. The line names that, and the action it names is one
    // that exists — overrideBudget has no route and no UI today, so telling
    // anyone to "raise the budget" would point at a dead end.
    return line(
      <span className="text-coral-700 font-semibold" data-testid="budget-exhausted-line">
        This Agreement's checking budget ran out before every criterion was checked — the rest need a person to review them.
        Flagged {fmtStamp(run.finished_at || run.started_at || run.created_at)}.
      </span>
    );
  }

  // A failed newest run surfaces as a structured, plain-words error — the
  // button must both work and look like it worked, and a dead pipeline must
  // never disappear silently (F2.1). WU-A′ §2: name the stage it died in,
  // what happened, when, and the next step — never a bare "internal error".
  if (run.status === 'failed') {
    const stage = {
      queued: 'before it could start',
      planning: 'while planning the check',
      collecting: 'while reading the evidence files',
      judging: 'while judging the evidence',
    }[run.failed_stage] || 'before it finished';
    const why = {
      interrupted: 'a restart cut it off',
      daily_cap: "today's checking budget ran out",
      provider_error: 'the checking service had a problem',
      provider_not_configured: 'checking is not connected in this environment yet',
      llm_schema_invalid: 'the checking agent returned an unusable result',
      judge_output_invalid: 'the checking agent returned an unusable result',
      plan_validation_failed: 'the checking agent returned an unusable plan',
    }[run.error_code];
    // When the cause is unknown, say the stage and stop.
    //
    // The old fallback read "something went wrong on our side" — filler at
    // the exact moment the customer most needs a fact, and a defect under
    // the copy bar. An unexplained failure gets a reference instead: the
    // one thing that turns "it broke again" into something we can trace.
    const reference = run.id ? String(run.id).slice(0, 8) : null;
    const next = run.error_code === 'daily_cap'
      ? 'It resumes automatically next period.'
      : isSeller ? '' : 'The agency can retry it.';
    return line(
      <>
        <span className="text-coral-700 font-semibold" data-testid="failed-run-line">
          The last check stopped {stage}{why ? ` — ${why}` : ''}. Stopped {fmtStamp(run.finished_at || run.started_at || run.created_at)}.{!why && reference ? ` Reference ${reference}.` : ''}{next ? ` ${next}` : ''}
        </span>
        {isSeller && run.error_code !== 'daily_cap' && (
          <button disabled={busy} onClick={retry}
            className="text-[12px] font-semibold px-2.5 py-1 rounded-lg bg-primary-600 text-white hover:bg-primary-700">Retry the check</button>
        )}
      </>
    );
  }

  if (run.status === 'queued') {
    if (run.queued_reason === 'daily_cap') {
      return line(<span>Checks resume tomorrow — today's checking budget was reached.</span>);
    }
    if (countdownActive) {
      const secs = Math.max(0, Math.ceil((new Date(run.not_before).getTime() - nowTick) / 1000));
      const mm = String(Math.floor(secs / 60)).padStart(1, '0');
      const ss = String(secs % 60).padStart(2, '0');
      return line(
        <>
          <span>Checking starts in <b className="tabular-nums">{mm}:{ss}</b> · queued {fmtStamp(run.created_at)}</span>
          {isSeller && (
            <button disabled={busy} onClick={startNow}
              className="text-[12px] font-semibold px-2.5 py-1 rounded-lg bg-primary-600 text-white hover:bg-primary-700">Start now</button>
          )}
        </>
      );
    }
    const since = new Date(run.not_before || run.created_at).getTime();
    if (nowTick - since > QUEUED_STUCK_MS) {
      return line(
        <>
          <span className="text-coral-700 font-semibold">This check is taking longer than expected — queued {fmtStamp(run.created_at)}.</span>
          {isSeller && (
            <button disabled={busy} onClick={retry}
              className="text-[12px] font-semibold px-2.5 py-1 rounded-lg bg-primary-600 text-white hover:bg-primary-700">Retry the check</button>
          )}
        </>
      );
    }
    return line(<span><b>Checking now</b> · queued {fmtStamp(run.created_at)}</span>);
  }

  // planning / collecting / judging
  const lastProgress = new Date(run.last_event_at || run.started_at || run.created_at).getTime();
  if (nowTick - lastProgress > MID_RUN_STALL_MS) {
    return line(
      <>
        <span className="text-coral-700 font-semibold">This check is taking longer than expected — started {fmtStamp(run.started_at || run.created_at)}.</span>
        {isSeller && (
          <button disabled={busy} onClick={retry}
            className="text-[12px] font-semibold px-2.5 py-1 rounded-lg bg-primary-600 text-white hover:bg-primary-700">Retry the check</button>
        )}
      </>
    );
  }
  return line(<span><b>Checking now</b> — started {fmtStamp(run.started_at || run.created_at)} by <b>Verdict Agent</b>.</span>);
}

// evidenceCtaShown (WU-C): true only when the card above is already showing
// "Add evidence" for this Outcome — with nothing uploaded, "Submit evidence
// for checking" is a verb the state cannot honour (an empty manifest
// short-circuits every criterion to needs_review). Exactly one primary verb
// renders per Outcome. Defaults false so every other caller is unchanged.
export default function VerificationActions({ row, isSeller, agreementId, onChanged, sellerName, buyerName, runs = [], onRunsChanged, evidenceCtaShown = false }) {
  const deliverables = (row && row.deliverables) || [];
  const [open, setOpen] = useState(null); // 'manual' | 'refute' | null
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // WU-A′ §1 — instant acknowledgment: the moment Submit is pressed this
  // renders a visible "requested" line, before any network round trip. It
  // clears when the refreshed run list takes over (the live progress row).
  const [submitAck, setSubmitAck] = useState(false);

  if (deliverables.length === 0) return null;

  // The freshest run covering THIS Outcome (its deliverable, or an
  // agreement-wide run).
  const deliverableIds = new Set(deliverables.map((d) => d.id));
  const rowRuns = (runs || [])
    .filter((r) => !r.deliverable_id || deliverableIds.has(r.deliverable_id))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  // The newest live run wins; else a failed newest run — but only while the
  // Outcome still awaits verification (a stale failure under an
  // already-verified Outcome is history, not an alert).
  const newestRun = rowRuns[0] || null;
  // budget_exhausted rides the same gate as failed: the run stopped without
  // a result, and the Outcome is still awaiting verification. Its criteria
  // land needs_review, so without this the customer gets a "Needs human
  // review" card that never names the budget as the reason.
  const STOPPED_WITHOUT_RESULT = ['failed', 'budget_exhausted'];
  const liveRun = rowRuns.find((r) => NON_TERMINAL.includes(r.status))
    || (newestRun && STOPPED_WITHOUT_RESULT.includes(newestRun.status) && row.status === 'defined' ? newestRun : null);

  const status = row.status;          // 'defined' | 'verified' | 'settled'
  const signoff = row.buyerSignoff;   // 'awaiting_buyer' | 'accepted' | 'refuted' | null
  const buyer = buyerName || 'the client';
  const seller = sellerName || 'the agency';

  const runAll = async (fn, { needNote, ack } = {}) => {
    if (needNote && note.trim().length < 10) { setErr('A note of at least 10 characters is required.'); return; }
    setBusy(true); setErr('');
    if (ack) setSubmitAck(true); // visible state NOW, before any network call
    try {
      for (const d of deliverables) await fn(d);
      setOpen(null); setNote('');
      // Refresh the run list first (the live progress row takes over from
      // the instant acknowledgment), then the P&L.
      if (onRunsChanged) await onRunsChanged();
      await onChanged();
    } catch (e) {
      const httpStatus = e.response?.status;
      if (httpStatus === 402) {
        setErr('Checking budget reached — the $500 cap on this Agreement was hit. Authorize overage or confirm delivery manually.');
      } else {
        setErr(e.response?.data?.message || e.response?.data?.error || 'Action failed — try again.');
      }
    } finally { setBusy(false); setSubmitAck(false); }
  };

  const submitEvidence = () => runAll(
    (d) => verificationRunsApi.trigger(agreementId, { deliverable_id: d.id }),
    { ack: true }
  );
  const manualOverride = () => runAll((d) => verificationApi.verifyManual(agreementId, d.id, note.trim()), { needNote: true });
  const accept = () => runAll((d) => verificationApi.accept(agreementId, d.id, idemKey()));
  const refute = () => runAll((d) => verificationApi.refute(agreementId, d.id, note.trim(), idemKey()), { needNote: true });

  const cancelNote = () => { setOpen(null); setNote(''); setErr(''); };

  const wrap = (inner) => (
    <div className="flex flex-col gap-2">
      {/* WU-A′ §1 — the sub-100ms acknowledgment: rendered from local state
          the instant Submit is pressed; the live run row replaces it. */}
      {submitAck && !liveRun && (
        <div className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12.5px] text-sky-800" data-testid="submit-ack">
          <span className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
          <span><b>Check requested</b> — it appears in the list below in a moment.</span>
        </div>
      )}
      {/* PRD-031 F2.1 — the live check state (countdown / Checking now /
          cap-wait / taking-longer), truthful for both roles. */}
      <RunStateLine run={liveRun} isSeller={isSeller} agreementId={agreementId} onRunsChanged={onRunsChanged || onChanged} />
      <div className="flex items-center gap-2.5 flex-wrap">{inner}</div>
      {err && <p className="text-[11px] text-red-600">{err}</p>}
    </div>
  );

  // ----- SELLER -----------------------------------------------------------
  if (isSeller) {
    if (status === 'settled') return wrap(<Note>Verified · <b>accepted by {buyer}</b> · settled.</Note>);
    if (status === 'defined') {
      return wrap(
        <>
          {!evidenceCtaShown && !(liveRun && NON_TERMINAL.includes(liveRun.status)) && (
            <button disabled={busy} onClick={submitEvidence} data-testid="submit-evidence"
              className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-70">
              {busy ? 'Running…' : 'Run check'}
            </button>
          )}
          {/* PRD-031 E6 — no underline affordances inside the grid. */}
          {open !== 'manual' && (
            <button onClick={() => { setOpen('manual'); setErr(''); }} className="text-[11.5px] font-semibold text-gray-600 hover:text-primary-600">Confirm delivery manually</button>
          )}
          {open === 'manual' && (
            <NoteBox placeholder="Say why you’re confirming this without an automatic check (at least 10 characters)." submitLabel="Confirm delivery"
              onSubmit={manualOverride} note={note} setNote={setNote} busy={busy} onCancel={cancelNote} />
          )}
        </>
      );
    }
    // verified
    if (signoff === 'refuted') {
      return wrap(
        <>
          <Note><b>Sent back by {buyer}.</b> {row.refuteNote ? `“${row.refuteNote}” ` : ''}Re-submit evidence and the Verdict Agent checks it again.</Note>
          <button disabled={busy} onClick={submitEvidence}
            className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-70">
            {busy ? 'Submitting…' : 'Re-submit evidence for checking'}
          </button>
        </>
      );
    }
    if (signoff === 'accepted') return wrap(<Note>Delivered · <b>accepted by {buyer}</b> · ready to settle.</Note>);
    return wrap(<Note><b>Confirmed by the Verdict Agent</b> · waiting on <b>{buyer}</b> to accept.</Note>);
  }

  // ----- BUYER ------------------------------------------------------------
  if (status === 'settled') return wrap(<Note>Verified and accepted · settled.</Note>);
  if (status === 'defined') return wrap(<Note>The <b>Verdict Agent</b> checks evidence as {seller} adds it. Nothing is needed from you yet.</Note>);
  // verified
  if (signoff === 'accepted') return wrap(<Note>You accepted this. Counts toward settlement once every Outcome is accepted.</Note>);
  if (signoff === 'refuted') {
    return wrap(<Note>You sent this back. Settlement is paused until {seller} re-submits and a fresh check passes.</Note>);
  }
  // awaiting_buyer
  return wrap(
    <>
      <Note>The <b>Verdict Agent</b> confirmed this delivery. Review the evidence, then:</Note>
      <button disabled={busy} onClick={accept}
        className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">Accept delivery</button>
      {open !== 'refute' && (
        <button onClick={() => { setOpen('refute'); setErr(''); }} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50">Something’s off — send back</button>
      )}
      {open === 'refute' && (
        <NoteBox placeholder={`Tell ${seller} what looks wrong (at least 10 characters). This pauses settlement for this outcome and sends it back.`} submitLabel="Send back with note" tone="red"
          onSubmit={refute} note={note} setNote={setNote} busy={busy} onCancel={cancelNote} />
      )}
    </>
  );
}
